/**
 * Taking a creator or vendor off a campaign mid-flight (IN-1821 item 3).
 *
 * A hard delete is wrong once money exists, so the endpoint has three
 * outcomes and so does this: remove a line nobody has invoiced against,
 * withdraw one that has been invoiced, and refuse one that has been paid.
 *
 * Refusing on the paid case is not a reversal of D6. D6 governs what a
 * creator may invoice and says flag rather than block. This governs whether
 * staff may erase the record of money that has already left.
 *
 * The plan here is for the wording of the question we put to staff. The
 * server decides what actually happened and `removalOutcome` reads that back,
 * because a line's invoices can change between the dialog and the request.
 *
 * Pure.
 */

export const OUTCOMES = {
  REMOVED: 'removed',
  WITHDRAWN: 'withdrawn',
};

const text = (value) => String(value ?? '').trim();

const invoicesOf = (row) => (Array.isArray(row.invoices) ? row.invoices : []);

const paidOn = (row) =>
  row.invoice_status === 'paid' || invoicesOf(row).some((i) => i && i.status === 'paid');

/**
 * Whether anything has ever been invoiced against this line.
 *
 * A rejected invoice counts here, which is the opposite of how `splits` reads
 * it -- and both are right, because they are answering different questions.
 * `splits` asks how much has been committed, and a rejection commits nothing.
 * This asks whether a record exists, and a rejection is still a record. The
 * backend withdraws any line carrying a non-deleted invoice for the same
 * reason, so predicting otherwise would put the wrong word on the button.
 */
const invoicedOn = (row) =>
  text(row.invoice_status) !== '' || invoicesOf(row).length > 0;

/** Whoever this line pays: a creator by name, or the vendor it stands for. */
export function payeeName(row) {
  const line = row || {};
  return text(line.influencer_name) || text(line.vendor_name) || text(line.name) || 'this line';
}

/** True once the API has marked the line withdrawn. */
export function isWithdrawn(row) {
  return text((row || {}).status).toLowerCase() === 'withdrawn';
}

/**
 * What removal is expected to do, and how to ask for it.
 *
 * `refuse` means do not offer the action at all; `body` says why, so the
 * disabled control can explain itself rather than just being grey.
 */
export function removalPlan(row) {
  const line = row || {};
  const who = payeeName(line);

  if (!row) {
    return {
      action: 'refuse',
      title: 'Remove from this campaign',
      body: 'This line could not be read.',
      confirmLabel: 'Remove',
    };
  }

  if (isWithdrawn(line)) {
    return {
      action: 'refuse',
      title: `Remove ${who}`,
      body: `${who} has already been withdrawn from this campaign.`,
      confirmLabel: 'Remove',
    };
  }

  if (paidOn(line)) {
    return {
      action: 'refuse',
      title: `Remove ${who}`,
      body: `${who} cannot be removed: an invoice on this line has already been paid.`,
      confirmLabel: 'Remove',
    };
  }

  if (invoicedOn(line)) {
    return {
      action: 'withdraw',
      title: `Withdraw ${who}`,
      // Says only what the backend actually does. An earlier draft promised
      // the line would stop counting towards campaign spend; it does not --
      // a withdrawn line's invoices still total in the dashboard summary and
      // the spend rollup, and can still be approved.
      body:
        `${who} has already invoiced, so the line is withdrawn rather than deleted. The `
        + 'invoices are kept and stay readable. The line cannot raise new invoices, and '
        + 'its invoices cannot go into a payment run.',
      confirmLabel: 'Withdraw',
    };
  }

  return {
    action: 'remove',
    title: `Remove ${who}`,
    body: `${who} has not invoiced, so the line is removed outright.`,
    confirmLabel: 'Remove',
  };
}

/**
 * What the server did.
 *
 * An unrecognised body is read as a withdrawal, which is the safer of the
 * two: dropping the row would tell staff a deletion happened that may not
 * have, and a line wrongly left on screen is corrected by a refresh.
 */
export function removalOutcome(body) {
  const sent = text((body || {}).result).toLowerCase();
  const removed = sent === OUTCOMES.REMOVED;

  return {
    outcome: removed ? OUTCOMES.REMOVED : OUTCOMES.WITHDRAWN,
    row: removed ? null : body || null,
    message: removed
      ? 'Line removed from the campaign'
      : 'Line withdrawn. Its invoices are kept.',
  };
}

/**
 * Whether this line can be put back on the campaign.
 *
 * Only a withdrawn creator line. A vendor line has no `influencer_id`, and
 * whether re-posting a vendor name reuses the line or creates a second one is
 * not confirmed — so the action is not offered rather than guessed at.
 */
export function canRestore(row) {
  const line = row || {};
  return isWithdrawn(line) && Boolean(line.influencer_id);
}

/**
 * The body that puts a withdrawn line back.
 *
 * `POST .../campaigns/{id}/creators` with the same `influencer_id` reuses the
 * existing line rather than creating a second one, sets it back to
 * `approved`, and leaves the prices alone unless the call sends new ones
 * (confirmed with the backend, 29 Oct). So this carries the id and nothing
 * else: any price in the body would overwrite one agreed before the
 * withdrawal, which is the thing the withdrawal was careful to keep.
 */
export function restorePayload(row) {
  const line = row || {};
  return line.influencer_id ? { influencer_id: line.influencer_id } : null;
}
